<template>
  <div>
    <!-- Banner Section -->
    <section class="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      <div
        v-for="(slide, index) in banners"
        :key="index"
        class="absolute inset-0 transition-opacity duration-700"
        :class="currentBannerSlide === index ? 'opacity-100' : 'opacity-0'"
      >
        <video
          v-if="slide.isVideo"
          :src="slide.media"
          :alt="slide.title"
          class="w-full h-full object-cover"
          autoplay
          loop
          muted
          playsinline
        />
        <img
          v-else
          :src="slide.media"
          :alt="slide.title"
          class="w-full h-full object-cover"
        />
        <div class="absolute inset-0 bg-black/40"></div>
      </div>
      <div class="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
        <h1 class="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
          {{ $t('home.bannerTitle') }}
        </h1>
        <p class="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
          {{ $t('home.bannerSubtitle') }}
        </p>
        <router-link
          to="/"
          class="inline-block px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30"
        >
          {{ $t('home.explore') }}
        </router-link>
      </div>
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        <button
          v-for="(_, index) in banners"
          :key="index"
          @click="currentBannerSlide = index"
          class="w-3 h-3 rounded-full transition-all duration-300"
          :class="currentBannerSlide === index ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/80'"
        ></button>
      </div>
    </section>

    <!-- Categories Section -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-dark mb-4">{{ $t('home.categories') }}</h2>
          <div class="w-16 h-1 bg-primary mx-auto rounded-full"></div>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div
            v-for="category in categories"
            :key="category.id"
            class="group cursor-pointer"
            @click="$router.push(`/?category=${category.id}`)"
          >
            <div class="relative overflow-hidden rounded-2xl bg-gray-50 aspect-square mb-4">
              <img
                :src="category.icon"
                :alt="category.name"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div
                class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"
              ></div>
            </div>
            <h3 class="text-center font-semibold text-dark group-hover:text-primary transition-colors">
              {{ category.name }}
            </h3>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Products Section -->
    <section class="py-20 bg-light">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between mb-16">
          <div>
            <h2 class="text-3xl md:text-4xl font-bold text-dark mb-4">{{ $t('home.featured') }}</h2>
            <div class="w-16 h-1 bg-primary rounded-full"></div>
          </div>
          <router-link
            to="/"
            class="hidden md:inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
          >
            {{ $t('home.viewAll') }}
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </router-link>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div
            v-for="product in featuredProducts"
            :key="product.id"
            class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
            @click="$router.push(`/product/${product.id}`)"
          >
            <div class="relative overflow-hidden aspect-[4/3]">
              <img
                :src="product.image"
                :alt="product.name"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div
                class="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300"
              ></div>
            </div>
            <div class="p-5">
              <h3 class="font-semibold text-dark mb-2 line-clamp-1 group-hover:text-primary transition-colors">
                {{ product.name }}
              </h3>
              <p class="text-primary font-bold text-lg">{{ product.price }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Company Overview -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="relative">
            <img
              :src="'/uploads/about/company.jpg'"
              alt="Company"
              class="rounded-2xl shadow-2xl w-full"
            />
            <div class="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-full -z-10"></div>
            <div class="absolute -top-6 -left-6 w-32 h-32 bg-accent/10 rounded-full -z-10"></div>
          </div>
          <div>
            <h2 class="text-3xl md:text-4xl font-bold text-dark mb-6">{{ $t('home.companyTitle') }}</h2>
            <p class="text-secondary leading-relaxed mb-6">{{ $t('home.companyDesc1') }}</p>
            <p class="text-secondary leading-relaxed mb-8">{{ $t('home.companyDesc2') }}</p>
            <router-link
              to="/"
              class="inline-flex items-center gap-2 px-6 py-3 bg-dark text-white rounded-full hover:bg-dark/90 transition-colors"
            >
              {{ $t('home.companyLink') }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/api'

const { locale } = useI18n()

interface Category {
  id: number
  name: string
  icon: string
}

const categories = ref<Category[]>([])
const banners = ref<any[]>([])
const currentBannerSlide = ref(0)
let autoPlayInterval: ReturnType<typeof setInterval>

async function loadBanners() {
  try {
    const res: any = await api.get('/banners')
    if (res.code === 0 && Array.isArray(res.data)) {
      banners.value = res.data.map((b: any) => {
        const mediaUrl = b.image || ''
        const isVideo = /\.(mp4|webm|mov)$/i.test(mediaUrl)
        return {
          media: mediaUrl,
          title: b.title_ru || b.title_en || b.title,
          isVideo,
        }
      })
    }
  } catch (e) {
    console.error('Failed to load banners', e)
  }
}

async function loadCategories() {
  try {
    const res: any = await api.get('/categories', {
      headers: { 'Accept-Language': locale.value }
    })
    if (res.code === 0 && Array.isArray(res.data)) {
      categories.value = res.data
    }
  } catch (e) {
    console.error('Failed to load categories', e)
  }
}

onMounted(() => {
  loadCategories()
  loadBanners()
  autoPlayInterval = setInterval(() => {
    currentBannerSlide.value = (currentBannerSlide.value + 1) % banners.value.length
  }, 10000)
})

watch(locale, () => {
  loadCategories()
})

onUnmounted(() => {
  clearInterval(autoPlayInterval)
})

const featuredProducts = [
  { id: 1, name: '智能手表 Pro', price: '¥2,999', image: '/uploads/products/product1.jpg' },
  { id: 2, name: '无线降噪耳机', price: '¥1,299', image: '/uploads/products/product2.jpg' },
  { id: 3, name: '极简台灯', price: '¥399', image: '/uploads/products/product3.jpg' },
  { id: 4, name: '皮质背包', price: '¥899', image: '/uploads/products/product4.jpg' }
]
</script>
